import {
    Button,
    ButtonGroup,
    Card,
    Grid,
    Heading,
    Input,
    IOption,
    Separator,
} from '@design-systems/canvas-ui'
import { useState, useMemo, useEffect } from 'react'
import {
    HouseIcon,
    InfoFillIcon,
    PaperPlaneIcon,
    ProfileIcon,
    StoreIcon,
    TextStringIcon,
} from '@design-systems/react-icons'

import { SplitRow } from 'components/grid'
import { MPMLayout } from 'components/layout'
import { useUpdatePreferences } from '../../hooks/user-context/useUpdatePreferences'
import { useCreatePreferences } from '../../hooks/user-context/useCreatePreferences'
import { ErrorBoundary } from 'react-error-boundary'
import { ErrorAlert, ErrorFallback } from 'components/error-fallback'
import { TRANSLATION_KEYS } from 'locales/translation-keys'
import { useEssentials } from 'hooks/utils/useEssentials'
import { useLogClick } from 'utils/analytics'
import { safeTgtLogger } from 'utils/safeTgtLogger'
import { getDeviceSummary } from 'utils/helpers'
import { PreferredMap } from 'types/user-context'
import {
    getAllNavigationOptions,
    getAvailableNavigationOptions,
} from './helpers'
import { useFlags } from '../../firebase/FirebaseRemoteConfig.provider'
import './settings.css'

export const SettingsPage = () => {
    const { settings } = TRANSLATION_KEYS
    const {
        t,
        i18n,
        location,
        env,
        session,
        preferences,
        setPreferences,
        greenfieldGroups,
        locationType,
    } = useEssentials()
    const { logClick } = useLogClick('settings')
    const { landingPageEnabled } = useFlags()
    const { firstName, lanId } = session?.userInfo ?? {
        firstName: '',
        lanId: '',
    }

    const deviceSummary = getDeviceSummary()
    const isIphone = deviceSummary.device === 'iPhone'

    const [language, setLanguage] = useState<string>(
        preferences?.user_contexts?.[0]?.app_context?.language || 'en',
    )

    const [preferredMap, setPreferredMap] = useState<PreferredMap>(
        preferences?.user_contexts?.[0]?.app_context?.preferredMap ||
            (isIphone ? 'Apple' : 'Google'),
    )

    const [landingPage, setLandingPage] = useState<string>(
        preferences?.user_contexts?.[0]?.app_context?.landingPage ||
            '/sales/overview',
    )

    useEffect(() => {
        const savedLandingPage =
            preferences?.user_contexts?.[0]?.app_context?.landingPage
        if (savedLandingPage && savedLandingPage !== landingPage) {
            setLandingPage(savedLandingPage)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [preferences])

    const allNavigationOptions = useMemo(() => getAllNavigationOptions(t), [t])
    const availableNavigationOptions = useMemo(
        () =>
            getAvailableNavigationOptions({
                allOptions: allNavigationOptions,
                env,
                greenfieldGroups,
                locationType,
            }),
        [allNavigationOptions, env, greenfieldGroups, locationType],
    )

    // Check if saved landing page is still accessible
    useEffect(() => {
        if (availableNavigationOptions.length > 0) {
            const isLandingPageAvailable = availableNavigationOptions.some(
                (option) => option.value === landingPage,
            )
            if (!isLandingPageAvailable) {
                setLandingPage('/sales/overview')
            }
        }
    }, [availableNavigationOptions, landingPage])

    const {
        createPreferences,
        isError: isCreateError,
        isLoading: isCreateLoading,
        error: createError,
    } = useCreatePreferences({
        lanId,
        env,
        accessToken: session?.accessToken ?? '',
    })

    const {
        updatePreferences,
        isError: isUpdateError,
        isLoading: isUpdateLoading,
        error: updateError,
    } = useUpdatePreferences({
        lanId,
        env,
        accessToken: session?.accessToken ?? '',
        preferences,
    })

    const handleSuccess = (response: any) => {
        const newPreferences = {
            ...preferences,
            user_contexts: [response],
        }
        setPreferences(newPreferences)
    }

    const handleLanguageChange = (lang: string) => {
        if (language === lang) {
            return
        }
        setLanguage(lang)

        i18n.changeLanguage(lang)
            .catch((error) => {
                safeTgtLogger.warn(`Failed to change language to ${lang}`, {
                    error: {
                        message: error?.message || 'Language change failed',
                        type: error?.name || 'Error',
                        stack_trace: error?.stack || 'No stack trace available',
                    },
                    event: {
                        action: 'language_change_failed',
                        category: 'user_preferences',
                        outcome: 'failure',
                    },
                    nonIndexedFields: { lang },
                })
            })
            .finally(() => {
                const payload = {
                    language: lang,
                    preferredMap: preferredMap,
                    landingPage: landingPage,
                }
                if (preferences.user_contexts.length === 0) {
                    createPreferences(payload, {
                        onSuccess: handleSuccess,
                    })
                } else {
                    updatePreferences(payload, {
                        onSuccess: handleSuccess,
                    })
                }
            })
    }

    const handleMapChange = (map: PreferredMap) => {
        if (preferredMap === map) {
            return
        }
        setPreferredMap(map)
        logClick(`change preferred map to ${map}`)

        const payload = {
            language: language,
            preferredMap: map,
            landingPage: landingPage,
        }

        if (preferences.user_contexts.length === 0) {
            createPreferences(payload, {
                onSuccess: handleSuccess,
            })
        } else {
            updatePreferences(payload, {
                onSuccess: handleSuccess,
            })
        }
    }

    const handleLandingPageChange = (page: string) => {
        if (landingPage === page) {
            return
        }
        setLandingPage(page)
        logClick(`change landing page to ${page}`)

        const payload = {
            language: language,
            preferredMap: preferredMap,
            landingPage: page,
        }

        if (preferences.user_contexts.length === 0) {
            createPreferences(payload, {
                onSuccess: handleSuccess,
            })
        } else {
            updatePreferences(payload, {
                onSuccess: handleSuccess,
            })
        }
    }

    return (
        <ErrorBoundary
            fallbackRender={({ error, resetErrorBoundary }) => (
                <ErrorFallback
                    error={error}
                    resetErrorBoundary={resetErrorBoundary}
                    heading={t(settings.title)}
                />
            )}
        >
            <MPMLayout
                heading={t(settings.title)}
                className="settings-content"
                isBottomNavDisplayed={false}
            >
                <>
                    {(isCreateError || isUpdateError) && (
                        <ErrorAlert
                            className="hc8-mv-4x"
                            error={
                                (isCreateError
                                    ? createError
                                    : updateError) as Error
                            }
                        />
                    )}
                </>
                <Card className="hc8-pa-1x">
                    <Heading size={4}>{t(settings.info)}</Heading>
                    <Separator className="hc8-pb-3x" />
                    <Grid.Container justify="start" align="center">
                        <SplitRow
                            icon={ProfileIcon}
                            start={t(settings.you)}
                            end={`${firstName} (${lanId})`}
                        />
                        <SplitRow
                            icon={StoreIcon}
                            start={t(settings.location)}
                            end={`${location.shortName} ${location.name}`}
                        />
                        <SplitRow
                            icon={InfoFillIcon}
                            start={t(settings.version)}
                            end={`${process.env.REACT_APP_VERSION}`}
                        />
                        <SplitRow
                            icon={TextStringIcon}
                            start={t(settings.language)}
                            end={
                                <ButtonGroup>
                                    <Button
                                        className={`ghost ${language === 'en' ? 'lang-active' : ''}`}
                                        onClick={() => {
                                            logClick(
                                                'change language to English',
                                            )
                                            handleLanguageChange('en')
                                        }}
                                        isLoading={
                                            isCreateLoading || isUpdateLoading
                                        }
                                    >
                                        {t(settings.languages.EN)}
                                    </Button>
                                    <Button
                                        className={`ghost ${language === 'es' ? 'lang-active' : ''}`}
                                        onClick={() => {
                                            logClick(
                                                'change language to Spanish',
                                            )
                                            handleLanguageChange('es')
                                        }}
                                        isLoading={
                                            isCreateLoading || isUpdateLoading
                                        }
                                    >
                                        {t(settings.languages.ES)}
                                    </Button>
                                </ButtonGroup>
                            }
                        />
                        {isIphone && (
                            <SplitRow
                                icon={PaperPlaneIcon}
                                start={t(settings.map)}
                                paddingClass="hc8-pr-none"
                                end={
                                    <ButtonGroup>
                                        <Button
                                            className={`ghost ${preferredMap === 'Google' ? 'lang-active' : ''}`}
                                            onClick={() =>
                                                handleMapChange('Google')
                                            }
                                            isLoading={
                                                isCreateLoading ||
                                                isUpdateLoading
                                            }
                                        >
                                            {t(settings.maps.google)}
                                        </Button>
                                        <Button
                                            className={`ghost ${preferredMap === 'Apple' ? 'lang-active' : ''}`}
                                            onClick={() =>
                                                handleMapChange('Apple')
                                            }
                                            isLoading={
                                                isCreateLoading ||
                                                isUpdateLoading
                                            }
                                        >
                                            {t(settings.maps.apple)}
                                        </Button>
                                    </ButtonGroup>
                                }
                            />
                        )}
                        {landingPageEnabled && (
                            <SplitRow
                                icon={HouseIcon}
                                start={t(settings.landingPage)}
                                end={
                                    <Input.Select
                                        id="landing-page-select"
                                        value={landingPage}
                                        onChange={(
                                            option: IOption | undefined,
                                        ) => {
                                            handleLandingPageChange(
                                                String(option?.value ?? ''),
                                            )
                                        }}
                                        options={availableNavigationOptions.map(
                                            (option) => ({
                                                id: option.value,
                                                value: option.value,
                                                label: option.label,
                                                isDisabled: false,
                                            }),
                                        )}
                                        isDisabled={
                                            isCreateLoading ||
                                            isUpdateLoading ||
                                            availableNavigationOptions.length ===
                                                0
                                        }
                                        placeholder="Select an option"
                                    />
                                }
                            />
                        )}
                    </Grid.Container>
                </Card>
            </MPMLayout>
        </ErrorBoundary>
    )
}
